import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { 
  Users, 
  Clock,
  CreditCard,
  XCircle,
  Ban,
  DollarSign,
  Database,
  Zap,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import WinBackPanel from './WinBackPanel';
import EnvAuditPanel from './EnvAuditPanel';
import CostAnalyticsPanel from './CostAnalyticsPanel';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [emailMetrics, setEmailMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [statsRes, lifeRes, emailRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/metrics/lifecycle?days=30'),
        api.get('/admin/metrics/email?days=30'),
      ]);
      setStats(statsRes.data);
      setLifecycle(lifeRes.data);
      setEmailMetrics(emailRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Mock data for subscriber growth chart
  const mockGrowthData = lifecycle?.series?.map(d => ({
    month: d.date.slice(5),
    signups: d.signups,
    subscribes: d.subscribes,
  })) || [];

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'text-red-400',
      bgColor: 'bg-[#DC2626]/10',
      testId: 'total-users-card'
    },
    {
      title: 'Active Trials',
      value: stats?.active_trials || 0,
      icon: Clock,
      color: 'text-orange-400',
      bgColor: 'bg-orange-950/40',
      testId: 'active-trials-card'
    },
    {
      title: 'Active Subscriptions',
      value: stats?.active_subscriptions || 0,
      icon: CreditCard,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-950/40',
      testId: 'active-subscriptions-card'
    },
    {
      title: 'Expired Subscriptions',
      value: stats?.expired_subscriptions || 0,
      icon: XCircle,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-950/40',
      testId: 'expired-subscriptions-card'
    },
    {
      title: 'Cancelled Subscriptions',
      value: stats?.cancelled_subscriptions || 0,
      icon: Ban,
      color: 'text-amber-400',
      bgColor: 'bg-amber-950/40',
      testId: 'cancelled-subscriptions-card'
    },
    {
      title: 'Monthly Revenue',
      value: `$${stats?.estimated_monthly_revenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'text-slate-100',
      bgColor: 'bg-[#0F1730]/10',
      testId: 'monthly-revenue-card'
    },
    {
      title: 'Total Data Sources',
      value: stats?.total_data_sources || 0,
      icon: Database,
      color: 'text-purple-400',
      bgColor: 'bg-purple-950/40',
      testId: 'total-data-sources-card'
    },
    {
      title: 'Total Signals',
      value: stats?.total_signals || 0,
      icon: Zap,
      color: 'text-red-400',
      bgColor: 'bg-[#DC2626]/10',
      testId: 'total-signals-card'
    }
  ];

  if (loading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={`stat-skel-${i}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                  <Skeleton className="h-10 w-10 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-100 mb-2" data-testid="admin-dashboard-title">
          Admin Dashboard
        </h1>
        <p className="text-slate-300">
          Monitor platform statistics and user activity across dotbox.
        </p>
      </div>

      {/* Environment Secrets Health — pre-deploy self-check */}
      <div className="mb-8">
        <EnvAuditPanel />
      </div>

      {/* Cost Analytics — real-time P&L per user */}
      <div className="mb-8">
        <CostAnalyticsPanel />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map(({ title, value, icon: Icon, color, bgColor, testId }) => (
          <Card key={title} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300 mb-1">{title}</p>
                  <p className="text-2xl font-bold text-slate-100" data-testid={testId}>
                    {value}
                  </p>
                </div>
                <div className={`${bgColor} ${color} p-3 rounded-full`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Subscriber Growth Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-slate-100">Lifecycle Activity — Last 30 Days</CardTitle>
            <CardDescription>
              Daily signups and paid subscriptions — live data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mockGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="month" className="text-sm" interval={3} />
                  <YAxis className="text-sm" allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0F1730', 
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="signups" 
                    name="Signups"
                    stroke="#DC2626" 
                    strokeWidth={3}
                    dot={{ fill: '#DC2626', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 6, fill: '#0F1730' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="subscribes" 
                    name="Subscriptions"
                    stroke="#16A34A" 
                    strokeWidth={3}
                    dot={{ fill: '#16A34A', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 6, fill: '#16A34A' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-4 text-center text-xs">
              <div><div className="text-lg font-bold text-red-400" data-testid="lifecycle-total-signups">{lifecycle?.totals?.signups || 0}</div><div className="text-gray-500">Signups</div></div>
              <div><div className="text-lg font-bold text-emerald-400" data-testid="lifecycle-total-subscribes">{lifecycle?.totals?.subscribes || 0}</div><div className="text-gray-500">Subscribes</div></div>
              <div><div className="text-lg font-bold text-orange-400" data-testid="lifecycle-total-cancels">{lifecycle?.totals?.cancels || 0}</div><div className="text-gray-500">Cancels</div></div>
              <div><div className="text-lg font-bold text-amber-400" data-testid="lifecycle-total-deletes">{lifecycle?.totals?.deletes || 0}</div><div className="text-gray-500">Deletions</div></div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-slate-100">Quick Actions</CardTitle>
            <CardDescription>
              Common administrative tasks and management options
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              className="w-full justify-between bg-[#DC2626] hover:bg-[#DC2626]/90"
              onClick={() => navigate('/admin/subscribers')}
              data-testid="view-subscribers-button"
            >
              <span>View All Subscribers</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-between border-[#DC2626] text-red-400 hover:bg-[#DC2626]/10"
              onClick={() => navigate('/admin/payments')}
              data-testid="view-payments-button"
            >
              <span>Payment Management</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-between" 
              disabled
              data-testid="system-settings-button"
            >
              <span>System Settings</span>
              <span className="text-xs bg-[#1F2A4D] text-slate-300 px-2 py-1 rounded-full">
                Coming Soon
              </span>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Additional Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-red-400 mb-2">
              ${(stats?.estimated_monthly_revenue * 12)?.toFixed(2) || '0.00'}
            </div>
            <p className="text-sm text-slate-300">Estimated Annual Revenue</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-emerald-400 mb-2">
              {stats?.active_subscriptions && stats?.total_users 
                ? ((stats.active_subscriptions / stats.total_users) * 100).toFixed(1)
                : '0.0'
              }%
            </div>
            <p className="text-sm text-slate-300">Conversion Rate</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-slate-100 mb-2">
              {stats?.total_data_sources && stats?.total_users 
                ? (stats.total_data_sources / stats.total_users).toFixed(1)
                : '0.0'
              }
            </div>
            <p className="text-sm text-slate-300">Avg Data Sources per User</p>
          </CardContent>
        </Card>
      </div>

      {/* Email & System Health quick links */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-slate-100 flex items-center justify-between">
              <span>Email Delivery — Last 30 Days</span>
              <span className="text-xs font-normal text-gray-500">{emailMetrics?.success_rate_pct ?? 100}% success</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div><div className="text-2xl font-bold text-red-400" data-testid="email-total-sent">{emailMetrics?.total_sent || 0}</div><div className="text-xs text-gray-500">Sent</div></div>
              <div><div className="text-2xl font-bold text-emerald-400" data-testid="email-total-succeeded">{emailMetrics?.total_succeeded || 0}</div><div className="text-xs text-gray-500">Delivered</div></div>
              <div><div className="text-2xl font-bold text-amber-400" data-testid="email-total-failed">{emailMetrics?.total_failed || 0}</div><div className="text-xs text-gray-500">Failed</div></div>
            </div>
            {(emailMetrics?.by_category || []).length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs text-gray-500 mb-2">By category</div>
                <div className="flex flex-wrap gap-2">
                  {emailMetrics.by_category.slice(0, 6).map(c => (
                    <span key={c.category} className="px-2 py-1 bg-[#131C3A] rounded text-xs">
                      <strong>{c.count}</strong> {c.category}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-slate-100">System Health</CardTitle><CardDescription>Run a full diagnostic check on all backend services</CardDescription></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-300 mb-4">Dive into SendGrid quota, Stripe webhook health, MongoDB metrics, and scheduler status.</p>
            <Button className="w-full justify-between" onClick={() => navigate('/admin/system-health')} data-testid="open-system-health-btn">
              <span>Open System Health Page</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* iter51 — Win-back automation visibility */}
      <div className="mt-8">
        <WinBackPanel />
      </div>
    </div>
  );
};

export default AdminDashboard;